"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Check, Sparkles, Zap, Crown, Waves, Lock } from "lucide-react"
import { useAuth } from "@/lib/auth/context"
import { createClient } from "@/lib/supabase/client"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer"
import { ScrollArea } from "@/components/ui/scroll-area"
import { isWebPaymentEnabled, getPricingInfo } from "@/lib/utils/pricing"
import { toast } from "sonner"
import { getOfferings, purchasePackage, restorePurchases } from "@/lib/iap/revenuecat"

interface PaywallProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userNumber?: number
}

export function Paywall({ open, onOpenChange, userNumber }: PaywallProps) {
  const { isMobile, isNative, isIOS } = usePlatform()
  const [loading, setLoading] = useState(false)
  const [offerings, setOfferings] = useState<any>(null)
  
  useEffect(() => {
    if (isNative && open) {
      const fetchOfferings = async () => {
        try {
          const rcOfferings = await getOfferings()
          setOfferings(rcOfferings)
        } catch (e) {
          console.error("Failed to fetch RC offerings:", e)
        }
      }
      fetchOfferings()
    }
  }, [isNative, open])

  const webPaymentEnabled = isWebPaymentEnabled()
  const pricingInfo = getPricingInfo(userNumber || 1001) // Fallback to yearly if unknown
  
  const price = pricingInfo.priceFormatted
  const tier = pricingInfo.isLifetime ? 'lifetime' : 'yearly'
  const isEarlyBird = (userNumber || 1001) <= 250

  const handleSubscribe = async () => {
    if (isEarlyBird) {
      setLoading(true)
      triggerHaptic('success')
      try {
        const res = await fetch("/api/profile/claim-earlybird", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        })
        const data = await res.json()
        if (data.success) {
          toast.success("Welcome to Plus! Free lifetime access granted.")
          onOpenChange(false)
          window.location.reload()
        } else {
          throw new Error(data.error || "Failed to claim early bird access")
        }
      } catch (error: any) {
        console.error("Claim early bird error:", error)
        toast.error(error.message || "Failed to claim early bird access")
      } finally {
        setLoading(false)
      }
      return
    }

    if (!webPaymentEnabled && !isMobile) {
      // Redirect to app store or show instructions
      window.open("https://apps.apple.com/app/spotstosurf", "_blank")
      return
    }

    setLoading(true)
    triggerHaptic('impact')
    
    try {
      // If native, use RevenueCat
      if (isNative) {
        if (!offerings?.current) {
          toast.error("Subscriptions unavailable. Please try again later.")
          return
        }
        
        // Find the appropriate package (yearly or lifetime)
        const rcPackage = tier === 'lifetime' 
          ? offerings.current.lifetime 
          : offerings.current.annual || offerings.current.availablePackages[0]
          
        if (!rcPackage) {
          toast.error("Plan unavailable. Please try again later.")
          return
        }
        
        const result = await purchasePackage(rcPackage)
        if (result.success) {
          toast.success("Welcome to Plus!")
          onOpenChange(false)
          window.location.reload()
        } else if (result.cancelled) {
          toast.info("Purchase cancelled")
        }
        return
      }

      // Logic for Stripe or RevenueCat would go here
      // For now, we'll just redirect to Stripe checkout
      const res = await fetch("/api/stripe/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      })
      const data = await res.json()
      
      if (data.success && data.data?.url) {
        window.location.href = data.data.url
      } else {
        throw new Error(data.error || "Failed to create checkout session")
      }
    } catch (error) {
      console.error("Subscription error:", error)
      alert("Failed to start checkout. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const content = (
    <div className="flex flex-col items-center text-center space-y-6 pt-6">
      <div className="w-20 h-20 bg-blue-600 rounded-[2rem] flex items-center justify-center shadow-xl shadow-blue-600/20 rotate-3">
        <Crown className="w-10 h-10 text-white" />
      </div>
      
      <div className="space-y-2">
        <h2 className="text-3xl font-black text-slate-900 dark:text-white uppercase tracking-tight italic">Unlock Plus</h2>
        <p className="text-slate-500 dark:text-slate-400 font-medium italic">
          Join the crew and get the full SpotsToSurf experience.
        </p>
      </div>

      <div className="w-full space-y-3 pt-2">
        {[
          "10-Day Detailed Forecasts",
          "Real-time Community Chat",
          "Join & Organize Surf Parties",
          "Full Spot Insights & Attributes",
          "Advanced Activity Filters",
          "Unlimited Surf Plans"
        ].map((feat, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Check className="w-3 h-3 text-white stroke-[3]" />
            </div>
            <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{feat}</span>
          </div>
        ))}
      </div>

      <div className="w-full pt-4 space-y-4 pb-10">
        <div className="p-6 bg-gradient-to-br from-blue-600 to-blue-700 rounded-[2.5rem] shadow-xl shadow-blue-600/20 text-white relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform">
            <Zap className="w-20 h-20" />
          </div>
          
          <div className="relative z-10">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] mb-1 opacity-80">
              {isEarlyBird ? "Early Bird Free Offer" : tier === 'lifetime' ? "Founder Member Offer" : "Plus Membership"}
            </div>
            <div className="flex items-baseline justify-center gap-1 mb-1">
              <span className="text-4xl font-black">{isEarlyBird ? "FREE" : price}</span>
              <span className="text-sm font-bold opacity-80">
                {isEarlyBird ? "/ lifetime" : tier === 'lifetime' ? '/ lifetime' : '/ year'}
              </span>
            </div>
            <p className="text-[10px] font-bold opacity-70">
              {isEarlyBird || tier === 'lifetime' 
                ? "One-time payment. Never pay again." 
                : "Billed annually. Cancel anytime."}
            </p>
          </div>
        </div>

        {isEarlyBird && (
          <div className="py-2">
            <p className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] italic animate-pulse">
              🎁 You're in the first 250! Claim free lifetime access.
            </p>
          </div>
        )}

        <Button 
          onClick={handleSubscribe} 
          disabled={loading}
          className="w-full h-16 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-[2rem] font-black uppercase tracking-widest text-sm shadow-xl active:scale-[0.98] transition-all"
        >
          {loading ? "Preparing..." : isEarlyBird ? "Claim Free Access" : (!webPaymentEnabled && !isMobile ? "Download on App Store" : "Upgrade Now")}
        </Button>
        
        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest opacity-50 italic">
          {isNative || isIOS 
            ? "Secure payment via Apple App Store" 
            : webPaymentEnabled ? "Secure checkout via Stripe & Cards" : "Web payments coming soon • Upgrade via iOS"}
        </p>

        {isMobile && (
          <Button 
            variant="link" 
            onClick={async () => {
              triggerHaptic('impact')
              setLoading(true)
              toast.info("Restoring purchases...")
              try {
                const customerInfo = await restorePurchases()
                if (customerInfo?.entitlements.active["plus_membership"]) {
                  toast.success("Purchases restored successfully!")
                  onOpenChange(false)
                  window.location.reload()
                } else {
                  toast.info("No active subscriptions found.")
                }
              } catch (e) {
                toast.error("Restore failed. Please try again later.")
              } finally {
                setLoading(false)
              }
            }}
            className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em] h-auto p-0"
          >
            Restore Purchases
          </Button>
        )}
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="bg-white dark:bg-slate-900 border-none rounded-t-[3rem] h-[92vh]">
          <DrawerHeader className="sr-only">
            <DrawerTitle>Unlock Plus</DrawerTitle>
            <DrawerDescription>Upgrade your account to get full access</DrawerDescription>
          </DrawerHeader>
          <ScrollArea className="flex-1 w-full px-6">
            <div className="pb-12">
              {content}
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-none bg-white dark:bg-slate-900 rounded-[3rem] shadow-2xl max-h-[90vh]">
        <DialogTitle className="sr-only">Unlock Plus</DialogTitle>
        <DialogDescription className="sr-only">Upgrade your account to get full access</DialogDescription>
        <ScrollArea className="max-h-[90vh] px-6">
          <div className="pb-12">
            {content}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
